<template>
  <div id="app" :class="{ 'dark-mode': isDarkMode }">
    <nav v-if="showNavbar" class="navbar navbar-expand-lg navbar-dark bg-dark">
      <div class="container-fluid px-3">
        <router-link class="navbar-brand d-flex align-items-center" :to="isAuthed ? '/home' : '/'">
          <img src="@/assets/RJourneyWhiteIcon.png" alt="Pam Logo" style="width: 32px; height: 32px; margin-right: 10px;" />
          <span>Pam</span>
        </router-link>

        <button class="navbar-toggler border-0" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav" aria-controls="navbarNav" aria-expanded="false" aria-label="Toggle navigation">
          <span class="navbar-toggler-icon"></span>
        </button>

        <div class="collapse navbar-collapse" id="navbarNav">
          <!-- Main Navigation -->
          <ul class="navbar-nav me-auto mb-2 mb-lg-0">
            <li class="nav-item" v-if="isAuthed">
              <router-link class="nav-link" to="/home" @click="closeNavbar">
                <i class="bi bi-house-door me-1"></i>Home
              </router-link>
            </li>
            <li class="nav-item" v-if="isAuthed">
              <router-link class="nav-link" to="/dashboard" @click="closeNavbar">
                <i class="bi bi-speedometer2 me-1"></i>Dashboard
              </router-link>
            </li>

            <!-- Admin dropdown (desktop) / expandable section (mobile) -->
            <li class="nav-item" v-if="isAuthed && isAdmin">
              <!-- Desktop: Simple link to /admin -->
              <router-link class="nav-link d-none d-lg-block" to="/admin/users" @click="closeNavbar">
                <i class="bi bi-gear me-1"></i>Admin
              </router-link>

              <!-- Mobile: Expandable section in hamburger -->
              <div class="d-lg-none">
                <div class="nav-link text-muted small text-uppercase fw-bold mt-2 mb-1">Administration</div>
                <template v-for="item in adminItems" :key="item.path">
                  <!-- External URL -->
                  <a
                    v-if="item.externalUrl"
                    :href="item.externalUrl"
                    target="_blank"
                    class="nav-link ps-4"
                    @click="closeNavbar"
                  >
                    <i v-if="item.icon" :class="[item.icon, 'me-1']"></i>{{ item.title }}
                    <i class="bi bi-box-arrow-up-right ms-1 small"></i>
                  </a>
                  <!-- Internal Route -->
                  <router-link
                    v-else
                    class="nav-link ps-4"
                    :to="item.path"
                    @click="closeNavbar"
                  >
                    <i v-if="item.icon" :class="[item.icon, 'me-1']"></i>{{ item.title }}
                  </router-link>
                </template>
              </div>
            </li>
          </ul>

          <!-- User Controls -->
          <ul class="navbar-nav align-items-lg-center">
            <!-- Feedback Button (icon only) -->
            <li class="nav-item" v-if="isAuthed">
              <button
                class="icon-btn"
                @click="feedbackModalOpen = true; closeNavbar();"
                title="Send Feedback"
                aria-label="Send Feedback"
              >
                <span>💬</span>
              </button>
            </li>

            <!-- Theme Toggle (icon only) -->
            <li class="nav-item">
              <button
                class="icon-btn"
                @click="toggleDarkMode(); closeNavbar();"
                :title="isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'"
                :aria-label="isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'"
              >
                <span v-if="isDarkMode">☀️</span>
                <span v-else>🌙</span>
              </button>
            </li>

            <!-- User Info / Login -->
            <li class="nav-item" v-if="!isAuthed">
              <router-link class="nav-link" to="/login" @click="closeNavbar">
                <i class="bi bi-box-arrow-in-right me-1"></i>Login
              </router-link>
            </li>

            <li class="nav-item" v-else>
              <button class="nav-link btn btn-link text-light border-0 d-flex align-items-center user-btn" @click="settingsModalOpen = true; closeNavbar();" title="Settings">
                <i class="bi bi-person-circle me-2"></i>
                <span>{{ user?.name || user?.email || 'User' }}</span>
                <span v-if="isAdmin" class="badge bg-danger ms-2">Admin</span>
              </button>
            </li>

            <!-- Logout -->
            <li class="nav-item" v-if="isAuthed">
              <button class="nav-link btn btn-link text-light border-0 d-flex align-items-center" @click="doLogout(); closeNavbar();">
                <i class="bi bi-box-arrow-right me-2"></i>
                <span>Logout</span>
              </button>
            </li>
          </ul>
        </div>
      </div>
    </nav>

    <!-- System Status Banner (shows when in maintenance/degraded mode) -->
    <SystemStatusBanner v-if="isAuthed" />

    <router-view />

    <!-- Footer for authenticated pages -->
    <AppFooter v-if="isAuthed" />

    <!-- Idle warning modal -->
    <div v-if="idle.show" class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Session Expiring</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="stayLoggedIn"></button>
          </div>
          <div class="modal-body">
            <p>Your session will end due to inactivity in {{ idle.secondsLeft }} seconds.</p>
            <p>Please click "Stay Logged In" to continue your session.</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="doLogout">Logout Now</button>
            <button type="button" class="btn btn-primary" @click="stayLoggedIn">Stay Logged In</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="idle.show" class="modal-backdrop fade show"></div>

    <!-- Feedback Modal -->
    <FeedbackModal
      :open="feedbackModalOpen"
      @close="feedbackModalOpen = false"
    />

    <!-- Profile Settings Modal -->
    <ProfileSettingsModal v-model="settingsModalOpen" />

    <!-- Floating Feedback Button -->
    <FeedbackFab @open-feedback="feedbackModalOpen = true" />

  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, provide } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { IdleManager } from '@/utils/idle';
import { useAuthStore } from '@/store/auth';
import AppFooter from '@/components/AppFooter.vue';
import SystemStatusBanner from '@/components/SystemStatusBanner.vue';
import FeedbackModal from '@/components/FeedbackModal.vue';
import ProfileSettingsModal from '@/components/ProfileSettingsModal.vue';
import FeedbackFab from '@/components/FeedbackFab.vue';

export default {
  name: 'App',
  components: { AppFooter, SystemStatusBanner, FeedbackModal, ProfileSettingsModal, FeedbackFab },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const store = useAuthStore();
    // Initialize store from localStorage on load
    store.syncFromStorage();
    const isAuthed = computed(() => store.isAuthenticated);
    const user = computed(() => store.user);

    // Close mobile menu when clicking a nav link
    const closeNavbar = () => {
      const navbarCollapse = document.getElementById('navbarNav');
      if (navbarCollapse && navbarCollapse.classList.contains('show')) {
        // Use Bootstrap's Collapse API to properly close the menu
        const bsCollapse = window.bootstrap?.Collapse?.getInstance(navbarCollapse);
        if (bsCollapse) {
          bsCollapse.hide();
        } else {
          // Fallback: remove the 'show' class manually
          navbarCollapse.classList.remove('show');
        }
      }
    };

    const isAdmin = computed(() => {
      const roles = Array.isArray(user.value?.roles) ? user.value.roles : [];
      return user.value?.is_admin === true || roles.some(r => r.toLowerCase() === 'admin');
    });

    // Hide navbar on landing page when not authenticated
    // Use route directly instead of router.currentRoute for reactivity
    const showNavbar = computed(() => {
      // Always show navbar if authenticated
      if (isAuthed.value) return true;

      // Hide navbar on landing page (/) and login page when not authenticated
      if (route.path === '/' || route.path === '/login') {
        return false;
      }

      // Show navbar on other pages
      return true;
    });

    // Build admin items from router metadata
    const adminItems = computed(() => {
      const all = router.getRoutes();
      const parent = all.find(r => r.name === 'admin');
      if (!parent || !parent.children?.length) return [];
      return parent.children
        .filter(r => r.meta?.nav !== false)
        .map(r => ({
          title: r.meta?.title || r.name,
          path: r.path.startsWith('/') ? r.path : `${parent.path}/${r.path}`,
          icon: r.meta?.icon || '',
          externalUrl: r.meta?.externalUrl || null
        }));
    });

    // Dark mode state - default to dark mode
    const isDarkMode = ref(true);

    // Load theme preference from localStorage
    onMounted(() => {
      const savedTheme = localStorage.getItem('pam.theme');
      if (savedTheme !== null) {
        isDarkMode.value = savedTheme === 'dark';
      }
    });

    const toggleDarkMode = () => {
      isDarkMode.value = !isDarkMode.value;
      localStorage.setItem('pam.theme', isDarkMode.value ? 'dark' : 'light');
    };

    // Provide theme state to child components
    provide('isDarkMode', isDarkMode);

    const idle = ref({ show: false, secondsLeft: 60 });
    const feedbackModalOpen = ref(false);
    const settingsModalOpen = ref(false);
    const doLogout = () => {
      store.clearSession();
      // Use router push instead of window.location to avoid caching issues
      router.push({ path: '/', query: { loggedOut: 'true' } });
    };
    const stayLoggedIn = () => {
      idle.value = { show: false, secondsLeft: 60 };
    };

    // Initialize idle manager
    IdleManager.init({
      onWarnModal: ({ secondsLeft }) => { idle.value = { show: true, secondsLeft }; },
      onAutoLogout: () => { doLogout(); },
      onStayLoggedIn: () => { idle.value = { show: false, secondsLeft: 60 }; },
    });

    // Keep store in sync across tabs
    const storageListener = (e) => {
      if (e.key === 'auth.token' || e.key === 'auth.user') {
        store.syncFromStorage();
      }
    };
    onMounted(() => window.addEventListener('storage', storageListener));
    onBeforeUnmount(() => window.removeEventListener('storage', storageListener));

    return { isAuthed, isAdmin, user, adminItems, doLogout, idle, stayLoggedIn, isDarkMode, toggleDarkMode, closeNavbar, showNavbar, feedbackModalOpen, settingsModalOpen };
  }
}
</script>

<style>
/* Import admin design system */
@import '@/assets/admin-design-system.css';

/* ============================================
   PAM Theme Variables (RMS-style)
   ============================================ */

/* Dark Mode Theme Variables (Default) - truly dark */
#app.dark-mode {
  --bg-primary: #0d0f12;
  --bg-secondary: #16181d;
  --bg-card: #1a1d23;
  --text-primary: #e4e6eb;
  --text-secondary: #8b92a0;
  --border-color: #2d323a;
  --shadow: rgba(0, 0, 0, 0.8);
  --navbar-bg: #0a0c0f;
  --navbar-text: #e4e6eb;
}

/* Light Mode Theme Variables */
#app:not(.dark-mode) {
  --bg-primary: #f5f5f5;
  --bg-secondary: #ffffff;
  --bg-card: #ffffff;
  --text-primary: #2c3e50;
  --text-secondary: #7f8c8d;
  --border-color: #dee2e6;
  --shadow: rgba(0, 0, 0, 0.1);
  --navbar-bg: #212529;
  --navbar-text: #ffffff;
}

/* ============================================
   Base Styles
   ============================================ */

/* Base app styles */
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 100vh;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  transition: background-color 0.3s ease, color 0.3s ease;
}

/* Navbar - uses theme variables */
#app .navbar.bg-dark {
  background-color: var(--navbar-bg) !important;
}

/* Card styles - use theme variables */
#app .card {
  background-color: var(--bg-card);
  border-color: var(--border-color);
  color: var(--text-primary);
}

#app .card-header {
  background-color: var(--bg-secondary);
  border-bottom-color: var(--border-color);
  color: var(--text-primary);
}

#app .card-body {
  color: var(--text-primary);
}

/* Text colors */
#app .text-muted {
  color: var(--text-secondary) !important;
}

#app .text-secondary {
  color: var(--text-secondary) !important;
}

/* Borders - use theme variable */
#app .border,
#app .border-primary,
#app .border-primary-subtle,
#app .border-warning,
#app .border-danger,
#app .border-success,
#app .border-dark {
  border-color: var(--border-color) !important;
}

/* Preserve colored accents in dark mode */
#app.dark-mode .card-left-accent.border-primary-subtle {
  border-left: 4px solid #0d6efd !important;
}

#app.dark-mode .card-left-accent.border-warning {
  border-left: 4px solid #ffc107 !important;
}

#app.dark-mode .card-left-accent.border-danger {
  border-left: 4px solid #dc3545 !important;
}

#app.dark-mode .card-left-accent.border-success {
  border-left: 4px solid #198754 !important;
}

#app.dark-mode .card-left-accent.border-secondary {
  border-left: 4px solid #6c757d !important;
}

#app.dark-mode .card-left-accent.border-dark {
  border-left: 4px solid #495057 !important;
}

/* Form controls - use theme variables */
#app .form-select,
#app .form-control {
  background-color: var(--bg-secondary);
  border-color: var(--border-color);
  color: var(--text-primary);
}

#app .form-select:focus,
#app .form-control:focus {
  background-color: var(--bg-secondary);
  border-color: #0d6efd;
  color: var(--text-primary);
}

#app .input-group-text {
  background-color: var(--bg-secondary);
  border-color: var(--border-color);
  color: var(--text-primary);
}

/* Background utilities */
#app .bg-light {
  background-color: var(--bg-secondary) !important;
}

/* Special handling for badges with bg-light in dark mode */
#app.dark-mode .badge.bg-light {
  background-color: #4b5563 !important;
  color: #e4e6eb !important;
}

/* Dark mode alerts */
#app.dark-mode .alert {
  border-color: #3a3f4b;
}

#app.dark-mode .alert-warning {
  background-color: #3d3516;
  color: #ffc107;
  border-color: #665a1e;
}

#app.dark-mode .alert-danger {
  background-color: #3d1519;
  color: #f8d7da;
  border-color: #842029;
}

#app.dark-mode .alert-info {
  background-color: #0d2a3f;
  color: #b6d4fe;
  border-color: #084298;
}

/* Dark mode badges */
#app.dark-mode .badge {
  color: #fff;
}

#app.dark-mode .badge.bg-secondary {
  background-color: #4b5563 !important;
}

/* Modal - use theme variables */
#app .modal-content {
  background-color: var(--bg-card);
  color: var(--text-primary);
}

#app .modal-header,
#app .modal-footer {
  border-color: var(--border-color);
}

/* Dropdown - use theme variables */
#app .dropdown-menu {
  background-color: var(--bg-secondary);
  border-color: var(--border-color);
}

#app .dropdown-item {
  color: var(--text-primary);
}

#app .dropdown-item:hover,
#app .dropdown-item:focus {
  background-color: var(--bg-card);
  color: var(--text-primary);
}

/* Tables - use theme variables */
#app .table {
  color: var(--text-primary);
  border-color: var(--border-color);
  --bs-table-bg: transparent;
  --bs-table-striped-bg: rgba(255, 255, 255, 0.02);
  --bs-table-hover-bg: rgba(255, 255, 255, 0.05);
}

#app .table thead th {
  background-color: var(--bg-secondary);
  border-color: var(--border-color);
  color: var(--text-primary);
}

#app .table tbody tr {
  border-color: var(--border-color);
  background-color: transparent;
}

#app .table tbody td {
  background-color: transparent;
  border-color: var(--border-color);
  color: var(--text-primary);
}

/* Table stripes/hover kept as dark-mode specific for subtle effects */
#app.dark-mode .table-striped > tbody > tr:nth-of-type(odd) > * {
  background-color: rgba(255, 255, 255, 0.02);
  --bs-table-accent-bg: rgba(255, 255, 255, 0.02);
}

#app.dark-mode .table-hover > tbody > tr:hover > * {
  background-color: rgba(255, 255, 255, 0.05);
  color: var(--text-primary);
  --bs-table-accent-bg: rgba(255, 255, 255, 0.05);
}

#app .table td,
#app .table th {
  border-color: var(--border-color);
}

/* Admin sidebar - use theme variables */
#app .admin-sidebar {
  background-color: var(--navbar-bg);
  border-right-color: var(--border-color);
}

#app .side-link {
  color: var(--text-primary);
}

#app .side-link:hover {
  background-color: var(--bg-secondary);
  color: var(--text-primary);
}

#app .side-link.router-link-active {
  background-color: var(--bg-card);
  color: var(--text-primary);
}

/* Breadcrumb */
#app .breadcrumb {
  background-color: transparent;
}

#app .breadcrumb-item,
#app .breadcrumb-item a {
  color: var(--text-secondary);
}

#app .breadcrumb-item.active {
  color: var(--text-primary);
}

/* Dark mode home page hero */
#app.dark-mode .bg-light.rounded-3 {
  background-color: #242930 !important;
  border: 1px solid #3a3f4b;
}

/* Smooth transitions */
#app .card,
#app .form-select,
#app .form-control,
#app .modal-content,
#app .dropdown-menu,
#app .table {
  transition: background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease;
}

/* Card left accent utility */
.card-left-accent {
  border-left-width: 4px !important;
}

/* Icon-only buttons for navbar (feedback, theme toggle) */
.icon-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1.2rem;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  min-width: 40px;
  margin: 0 0.25rem;
}

.icon-btn:hover {
  background: rgba(52, 152, 219, 0.3);
  transform: scale(1.05);
}

.icon-btn:active {
  transform: scale(0.95);
}

.icon-btn span {
  font-size: 1.1rem;
  line-height: 1;
}

/* Mobile: icon buttons in hamburger menu */
@media (max-width: 991.98px) {
  .icon-btn {
    background: transparent;
    color: rgba(255, 255, 255, 0.85);
    padding: 0.5rem 1rem;
    margin: 0.25rem 0;
    width: 100%;
    justify-content: flex-start;
    border-radius: 0;
  }

  .icon-btn:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: none;
  }
}
</style>

<style>
/* User button in navbar */
.user-btn {
  cursor: pointer;
  transition: opacity 0.2s;
}

.user-btn:hover {
  opacity: 0.8;
}
</style>
